import { clear, h, isAbort, wait } from '../core/dom'
import { haptic } from '../core/haptics'
import { back } from '../core/router'
import { getSettings, markRitualDone, updateSettings } from '../core/store'
import { shutUp, speak } from '../core/voice'
import { MANTRAS, mantraText } from '../data/mantras'
import { getLang, t } from '../i18n'
import { topbar } from './shared'

/** Seconds between repetitions. Slower than feels natural, on purpose — the gap
 *  is where the sentence actually lands. */
const PACES = { slow: 13, medium: 9, fast: 6 } as const
type Pace = keyof typeof PACES

export function renderMantra(root: HTMLElement, signal: AbortSignal): void {
  signal.addEventListener('abort', shutUp, { once: true })
  pick(root, signal)
}

function pick(root: HTMLElement, signal: AbortSignal): void {
  clear(root)
  const d = t()
  const lang = getLang()
  const custom = getSettings().customMantra

  const input = h('input', {
    type: 'text',
    value: custom,
    placeholder: d.mantra.ownPlaceholder,
    maxLength: 90,
  })

  root.append(
    topbar(d.mantra.title),
    h(
      'div',
      { class: 'stack fade-in' },
      h('p', { class: 'section-label' }, d.mantra.pick),
      ...MANTRAS.map((m) =>
        h(
          'button',
          {
            class: 'tile',
            onclick: () => {
              haptic.tap()
              void run(root, signal, mantraText(m, lang))
            },
          },
          h('span', { class: 'label grow' }, mantraText(m, lang)),
        ),
      ),
      h('div', { class: 'divider' }),
      h(
        'div',
        { class: 'stack-sm' },
        h('label', { class: 'field' }, d.mantra.own),
        input,
        h(
          'button',
          {
            class: 'btn block',
            onclick: () => {
              const text = input.value.trim()
              if (!text) return
              haptic.tap()
              updateSettings({ customMantra: text })
              void run(root, signal, text)
            },
          },
          d.common.start,
        ),
      ),
    ),
  )
}

async function run(root: HTMLElement, signal: AbortSignal, text: string): Promise<void> {
  clear(root)
  const d = t()
  let pace: Pace = 'medium'

  const line = h('div', { class: 'mantra-text' }, text)

  const stage = h(
    'div',
    {
      class: 'mantra-stage',
      // The whole stage is the stop target. Hunting for a small button in the
      // dark, half asleep, is exactly the friction this screen should not have.
      onclick: () => {
        haptic.tap()
        markRitualDone('mantra')
        back()
      },
    },
    line,
  )

  const paceSeg = h(
    'div',
    { class: 'seg', style: { alignSelf: 'center' } },
    ...(['slow', 'medium', 'fast'] as Pace[]).map((p) =>
      h(
        'button',
        {
          class: p === pace ? 'on' : '',
          onclick: (e: Event) => {
            e.stopPropagation()
            pace = p
            haptic.tap()
            paceSeg.querySelectorAll('button').forEach((b, i) => {
              b.classList.toggle('on', (['slow', 'medium', 'fast'] as Pace[])[i] === p)
            })
          },
        },
        p === 'slow' ? d.mantra.slow : p === 'medium' ? d.mantra.medium : d.mantra.fast,
      ),
    ),
  )

  root.append(
    topbar(),
    stage,
    h(
      'div',
      { class: 'stack-sm', style: { paddingBottom: '8px' } },
      paceSeg,
      h('p', { class: 'faint center' }, d.mantra.tapToStop),
    ),
  )

  // The CSS keyframe is an 11s breathing cycle; scale it to the chosen pace so
  // the text swells and fades exactly once per repetition.
  try {
    for (;;) {
      const gap = PACES[pace]
      line.style.animationDuration = `${gap}s`
      void speak(text, { rate: 0.7, pitch: 0.85 })
      await wait(gap * 1000, signal)
    }
  } catch (err) {
    if (!isAbort(err)) throw err
  }
}
